import { captureError } from 'utils/captureError'
import { ApiError } from 'utils/errors'
import { logReject, logSuccess } from 'utils/logger'

interface IApiFactoryClassParams {
  baseUrl: string
}

interface IApiResponse<T, M = null> {
  status: number | string
  message: string
  data: T
  token: T
  totalCount?: number
  code: string
  meta?: M
  invoiceId?: number
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface IGetOptions {
  isLangAllowed?: boolean
}

class ApiFactory {
  baseUrl = ''
  isPending = false

  constructor({ baseUrl }: IApiFactoryClassParams) {
    this.baseUrl = baseUrl
    this.isPending = false
  }

  async apiFetcher<T>(
    url: string,
    options: RequestInit = {
      credentials: 'include'
    }
  ): Promise<IApiResponse<T>> {
    const startMoment = Date.now()
    this.isPending = true

    const request = new Request(url, options)

    // Helper function to safely parse body for logging
    const getBodyForLogging = () => {
      if (!options.body) return undefined
      if (options.body instanceof FormData) {
        return '[FormData]' // Or return some FormData info if you want
      }
      return JSON.parse(options.body as string)
    }

    const successLogger = this.successLogger.bind(
      this,
      startMoment,
      request,
      getBodyForLogging()
    )

    return fetch(request)
      .then(async (res): Promise<IApiResponse<T>> => {
        if (res.status === 204) {
          // Temporary fix for 204 status
          return this.successHandler<T>(
            {
              status: 204,
              message: 'No Content',
              data: {} as T,
              token: {} as T,
              code: '204'
            },
            successLogger
          )
        }

        const totalCount = res.headers.get('X-Total-Count')
        const json = await res.json()

        const data = totalCount
          ? {
              ...json,
              totalCount: parseInt(totalCount)
            }
          : json

        return this.successHandler<T>(data, successLogger)
      })
      .catch((error): IApiResponse<T> => {
        logReject(
          `${request?.method ? request?.method : 'GET'}: ${request?.url.replace(
            'https://',
            ''
          )} - ${Date.now() - startMoment}ms`,
          {
            status: error.cause?.status,
            body: getBodyForLogging(), // Use the same helper here
            error
          }
        )
        return this.errorHandler(error)
      })
      .finally(() => {
        this.isPending = false
      })
  }

  get<T>(path: string): Promise<IApiResponse<T>> {
    const url = `${this.baseUrl}${path}`
    return this.apiFetcher(url)
  }

  post<T, P>(
    path: string,
    body?: P,
    headers = {},
    signal?: AbortSignal
  ): Promise<IApiResponse<T>> {
    // Check if body is FormData
    const isFormData = body instanceof FormData

    const options: RequestInit = {
      headers: {
        // Don't set Content-Type for FormData - browser will set it automatically with boundary
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...headers
      },
      credentials: 'include',
      method: 'POST',
      // Don't stringify FormData - pass it directly
      body: isFormData ? body : JSON.stringify(body),
      signal
    }

    return this.apiFetcher(`${this.baseUrl}${path}`, options)
  }

  patch<T, P>(path: string, body?: P): Promise<IApiResponse<T>> {
    const options: RequestInit = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      method: 'PATCH',
      body: JSON.stringify(body)
    }

    return this.apiFetcher(`${this.baseUrl}${path}`, options)
  }

  put<T, P>(path: string, body?: P): Promise<IApiResponse<T>> {
    const options: RequestInit = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      method: 'PUT',
      body: JSON.stringify(body)
    }

    return this.apiFetcher(`${this.baseUrl}${path}`, options)
  }

  delete<T, P>(path: string, body?: P): Promise<IApiResponse<T>> {
    const options: RequestInit = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      method: 'DELETE',
      credentials: 'include',
      body: JSON.stringify(body)
    }

    return this.apiFetcher(`${this.baseUrl}${path}`, options)
  }

  successHandler<T>(
    response: IApiResponse<T>,
    successLogger?: (response: IApiResponse<T>) => void
  ): IApiResponse<T> {
    const { status, message, code, meta } = response

    // this should be done in middleware
    if ((typeof status === 'number' && status >= 400) || status === 'error') {
      throw new Error(`API call failed. ${message}`, {
        cause: { status, code, meta }
      })
    }

    successLogger?.(response)
    //data can be undefined , for example getCSRFTokenApi response has not data property , it is token
    return response
  }

  errorHandler<T>(error: ApiError): T {
    if (error.cause?.status === 401) {
      window.openRFAuthModal(
        {
          initialView: 'signin',
          preventCloseOutsideClick: true
        },
        () => {
          window.location.reload()
        }
      )
      return new Promise(() => {}) as T
    }
    captureError(error)
    throw error
  }

  successLogger<B, D>(
    startMoment: number,
    request: Request,
    body: B | undefined = undefined,
    response: IApiResponse<D>
  ) {
    logSuccess(
      `${request?.method ? request?.method : 'GET'}: ${request?.url.replace(
        'https://',
        ''
      )} - ${Date.now() - startMoment}ms`,
      {
        status: response.status,
        body,
        response: response.data
      }
    )
  }
}

export default ApiFactory
